import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import KinAvatar from "./KinAvatar";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import {
  Drawer,
  DrawerContent,
  DrawerTrigger,
} from "@/components/ui/drawer";

const ChatHeader = ({ onLogoClick, leadingSlot }: { onLogoClick?: () => void; leadingSlot?: React.ReactNode }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [suggestion, setSuggestion] = useState("");
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // "Need a hand?" help sheet — separate entry point from the lightbulb.
  // Same Web3forms fire-and-forget pattern (subject "Kin Help Request"),
  // but with the robust await + res.ok check from GetHelp.tsx so a failed
  // send never eats her text. Her account email + user_id ride along so
  // she never types her own email.
  const [helpOpen, setHelpOpen] = useState(false);
  const [helpMessage, setHelpMessage] = useState("");
  const [helpSending, setHelpSending] = useState(false);
  // Radix/vaul restores keyboard focus to the trigger button when its sheet
  // closes. That's correct a11y behavior, but it leaves the browser's native
  // focus ring visibly sitting on the icon afterward. Blurring the trigger
  // right after close clears that programmatic focus without touching
  // focus-visible for real keyboard/tab navigation.
  const feedbackTriggerRef = useRef<HTMLButtonElement>(null);
  const helpTriggerRef = useRef<HTMLButtonElement>(null);

  const handleSubmit = async () => {
    if (!suggestion.trim() || submitting) return;
    setSubmitting(true);
    try {
      await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          access_key: "eabca370-87d5-4393-aab8-2f6e3778c2dc",
          subject: "Kin Feature Suggestion",
          message: suggestion,
          email: email.trim() || "not provided",
          timestamp: new Date().toISOString(),
        }),
      });

      // Also log suggestion to Supabase (additive; non-blocking on failure)
      try {
        if (user) {
          await supabase.from("response_feedback").insert({
            user_id: user.id,
            feedback_type: "suggestion",
            suggestion_text: suggestion,
            message_content: null,
          });
        }
      } catch {
        /* silent */
      }

      setSubmitted(true);
      setTimeout(() => {
        setOpen(false);
        setTimeout(() => {
          setSubmitted(false);
          setSuggestion("");
          setEmail("");
          setSubmitting(false);
        }, 300);
      }, 2500);
    } catch {
      setSubmitting(false);
    }
  };

  // 5-tap debug toggle: 5 taps on the Kin logo within 3 seconds toggles
  // localStorage.kbDebug, shows a toast, and arms the strip in place via the
  // `kin:kb-debug` event. On device the ?debug=1 URL param is inaccessible, so
  // this is the only way to flip the strip on.
  //
  // ⚠️ This lives in the CHAT header, so it is only reachable from the Chat
  // tab. Diagnosing another tab means arming it here first, then navigating —
  // the flag is localStorage, so it survives the trip.
  //
  // localStorage, NOT sessionStorage: KBDebugStrip only reads the flag when it
  // mounts, so the strip cannot appear without a reload — and a Median in-app
  // reload starts a fresh browsing context, which empties sessionStorage. The
  // flag was therefore always wiped by the very reload needed to act on it, and
  // the strip could never be turned on from a device at all. Matches the
  // localStorage.kbFallback escape hatch in useKeyboardOffset.ts.
  //
  // Because localStorage persists indefinitely, both toasts state how to
  // reverse the toggle — this is the only affordance for turning the strip
  // back off before recording a screen.
  const logoTapTimesRef = useRef<number[]>([]);
  const handleLogoClick = () => {
    const now = Date.now();
    logoTapTimesRef.current = logoTapTimesRef.current.filter((t) => now - t < 3000);
    logoTapTimesRef.current.push(now);
    if (logoTapTimesRef.current.length >= 5) {
      logoTapTimesRef.current = [];
      const next = localStorage.getItem("kbDebug") !== "1";
      if (next) {
        localStorage.setItem("kbDebug", "1");
        toast("Debug strip ON. Tap the X on it, or 5 taps here, to turn it off.");
      } else {
        localStorage.removeItem("kbDebug");
        toast("Debug strip OFF. 5 taps to turn it back on.");
      }
      // Arms KBDebugStrip in place. Without this the strip polls nothing until
      // the next mount, so turning it ON used to need an app reload — which on
      // Median discards the keyboard state you were trying to photograph.
      window.dispatchEvent(new Event("kin:kb-debug"));
      return; // don't also fire resetChat on the debug-toggle tap
    }
    onLogoClick?.();
  };

  const handleOpenChange = (val: boolean) => {
    setOpen(val);
    if (!val) {
      setTimeout(() => {
        setSubmitted(false);
        setSuggestion("");
        setEmail("");
        setSubmitting(false);
      }, 300);
    }
  };

  const handleHelpSubmit = async () => {
    const trimmed = helpMessage.trim();
    if (!trimmed || helpSending) return;
    setHelpSending(true);
    try {
      const res = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          access_key: "eabca370-87d5-4393-aab8-2f6e3778c2dc",
          subject: "Kin Help Request",
          // Her account email so she never types it — also the reply-to target.
          email: user?.email || "unknown",
          from_name: user?.email || "A Kin user",
          message: trimmed,
          user_id: user?.id || null,
          user_email: user?.email || null,
          timestamp: new Date().toISOString(),
        }),
      });

      const data = await res.json().catch(() => null);
      if (!res.ok || !data?.success) throw new Error("send failed");

      // Success — clear the field and close the sheet.
      setHelpMessage("");
      setHelpOpen(false);
      toast("Sent 💌 We'll get back to you soon.", {
        style: {
          background: "#FFFFFF",
          border: "1.5px solid #5050C8",
          fontFamily: "'DM Serif Display', serif",
          color: "#1C1A3E",
        },
        duration: 3000,
      });
    } catch {
      // Never eat her message — leave the text in place so she can retry.
      toast.error("Hmm, that didn't go through. Mind trying once more? 💜", {
        duration: 4000,
      });
    } finally {
      setHelpSending(false);
    }
  };

  return (
    <>
      <header
        className="fixed top-0 left-0 right-0 z-50 pb-3"
        style={{
          backgroundColor: "#F0F0FF",
          paddingTop: "calc(env(safe-area-inset-top, 0px) + 12px)",
        }}
      >
        <div style={{ maxWidth: "42rem", margin: "0 auto", paddingLeft: 16, paddingRight: 16, width: "100%" }} className="flex items-center gap-3">
          {leadingSlot}
          <button onClick={handleLogoClick} className="cursor-pointer flex-shrink-0" aria-label="New conversation">
            <KinAvatar size="md" variant="dark" />
          </button>
          <div className="flex flex-col flex-1 min-w-0 justify-center" style={{ gap: 2 }}>
            <h1 className="font-display text-foreground" style={{ fontSize: 22, lineHeight: 1 }}>
              Kin<sup className="align-super text-kin-slate" style={{ fontSize: 10, fontWeight: 600 }}>™</sup>
            </h1>
            <div
              className="font-body text-kin-periwinkle truncate"
              style={{ fontSize: 12, fontWeight: 600, lineHeight: 1.4, paddingBottom: 2 }}
            >
              Kinyoudoitall
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0" style={{ marginLeft: 12 }}>
            <Drawer open={open} onOpenChange={handleOpenChange}>
              <DrawerTrigger asChild>
                <button
                  ref={feedbackTriggerRef}
                  className="w-11 h-11 flex items-center justify-center rounded-full transition-colors group"
                  aria-label="Share feedback"
                >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" className="text-kin-slate group-hover:text-kin-periwinkle transition-colors" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M9 18h6" />
                    <path d="M10 22h4" />
                    <path d="M12 2a7 7 0 0 0-7 7c0 2.38 1.19 4.47 3 5.74V17a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1v-2.26c1.81-1.27 3-3.36 3-5.74a7 7 0 0 0-7-7z" />
                  </svg>
                </button>
              </DrawerTrigger>
              <DrawerContent
                className="border-0 max-w-2xl mx-auto left-0 right-0"
                style={{
                  backgroundColor: "#1C1A3E",
                  borderRadius: "20px 20px 0 0",
                }}
                onCloseAutoFocus={(e) => {
                  // Radix's default here is to move focus back onto the trigger
                  // button, which leaves its native focus ring visibly rendered.
                  // Suppress that restore and blur the trigger instead — real
                  // Tab-key navigation to the button afterward is unaffected.
                  e.preventDefault();
                  feedbackTriggerRef.current?.blur();
                }}
              >
                <div className="px-4 pt-2 pb-8 max-w-2xl mx-auto w-full">
                  {submitted ? (
                    <div key="submitted" className="flex items-center justify-center py-12">
                      <p style={{ fontFamily: "'DM Serif Display', serif", fontSize: 22, color: "#FFFFFF", textAlign: "center" }}>
                        You're amazing. Feedback sent 💜
                      </p>
                    </div>
                  ) : (
                    <div key="form">
                      <h2 style={{ fontFamily: "'DM Serif Display', serif", fontSize: 22, color: "#FFFFFF", marginBottom: 6 }}>
                        Got an idea for Kin?
                      </h2>
                      <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: 14, color: "#C8C8F4", marginBottom: 20, lineHeight: 1.5 }}>
                        Tell me what you wish I could do. I build what moms actually need.
                      </p>
                      <textarea
                        value={suggestion}
                        onChange={(e) => setSuggestion(e.target.value)}
                        placeholder="What do you wish Kin could do?"
                        rows={4}
                        style={{
                          width: "100%", backgroundColor: "#F0F0FF", borderRadius: 14, padding: "12px 14px",
                          fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: 14, color: "#1C1A3E",
                          border: "none", outline: "none", resize: "none", marginBottom: 12, minHeight: 100,
                        }}
                      />
                      <input
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="Your email (optional, get notified when it's built)"
                        type="email"
                        style={{
                          width: "100%", backgroundColor: "#F0F0FF", borderRadius: 14, padding: "12px 14px",
                          fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: 14, color: "#1C1A3E",
                          border: "none", outline: "none", marginBottom: 16,
                        }}
                      />
                      <button
                        onClick={handleSubmit}
                        disabled={!suggestion.trim() || submitting}
                        style={{
                          width: "100%", backgroundColor: "#5050C8", color: "#FFFFFF", borderRadius: 14,
                          padding: "14px 0", fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 600,
                          fontSize: 16, border: "none", height: 48,
                          cursor: suggestion.trim() && !submitting ? "pointer" : "not-allowed",
                          opacity: suggestion.trim() && !submitting ? 1 : 0.6,
                        }}
                      >
                        {submitting ? "Sending..." : "Send it 💜"}
                      </button>
                    </div>
                  )}
                </div>
              </DrawerContent>
            </Drawer>
            <Drawer open={helpOpen} onOpenChange={setHelpOpen}>
              <DrawerTrigger asChild>
                <button
                  ref={helpTriggerRef}
                  className="w-11 h-11 flex items-center justify-center rounded-full transition-colors group"
                  aria-label="Need help"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" className="text-kin-slate group-hover:text-kin-periwinkle transition-colors" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10" />
                    <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
                    <path d="M12 17h.01" />
                  </svg>
                </button>
              </DrawerTrigger>
              <DrawerContent
                className="border-0 max-w-2xl mx-auto left-0 right-0"
                style={{
                  backgroundColor: "#1C1A3E",
                  borderRadius: "20px 20px 0 0",
                }}
                onCloseAutoFocus={(e) => {
                  // Radix's default here is to move focus back onto the trigger
                  // button, which leaves its native focus ring visibly rendered.
                  // Suppress that restore and blur the trigger instead — real
                  // Tab-key navigation to the button afterward is unaffected.
                  e.preventDefault();
                  helpTriggerRef.current?.blur();
                }}
              >
                <div className="px-4 pt-2 pb-8 max-w-2xl mx-auto w-full">
                  <h2 style={{ fontFamily: "'DM Serif Display', serif", fontSize: 22, color: "#FFFFFF", marginBottom: 6 }}>
                    Need a hand?
                  </h2>
                  <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: 14, color: "#C8C8F4", marginBottom: 20, lineHeight: 1.5 }}>
                    Tell me what you're stuck on — I'll do my best to help, or get it to someone who can.
                  </p>
                  <textarea
                    value={helpMessage}
                    onChange={(e) => setHelpMessage(e.target.value)}
                    placeholder="What do you need help with?"
                    rows={4}
                    aria-label="What do you need help with?"
                    style={{
                      width: "100%", backgroundColor: "#F0F0FF", borderRadius: 14, padding: "12px 14px",
                      fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: 14, color: "#1C1A3E",
                      border: "none", outline: "none", resize: "none", marginBottom: 16, minHeight: 100,
                    }}
                  />
                  <button
                    onClick={handleHelpSubmit}
                    disabled={!helpMessage.trim() || helpSending}
                    style={{
                      width: "100%", backgroundColor: "#5050C8", color: "#FFFFFF", borderRadius: 14,
                      padding: "14px 0", fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 600,
                      fontSize: 16, border: "none", height: 48,
                      cursor: helpMessage.trim() && !helpSending ? "pointer" : "not-allowed",
                      opacity: helpMessage.trim() && !helpSending ? 1 : 0.6,
                    }}
                  >
                    {helpSending ? "Sending..." : "Send it 💜"}
                  </button>
                  <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: 11, color: "#9090A8", marginTop: 10, textAlign: "center" }}>
                    We'll reply to {user?.email || "your account email"} 💜
                  </p>
                </div>
              </DrawerContent>
            </Drawer>
            <button
              onClick={() => navigate("/settings")}
              className="w-11 h-11 flex items-center justify-center rounded-full hover:bg-accent/50 transition-colors"
              aria-label="Settings"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#54525C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z" />
                <circle cx="12" cy="12" r="3" />
              </svg>
            </button>
          </div>
        </div>
      </header>
    </>
  );
};

export default ChatHeader;
